import { hashCode } from "./block-wrapper/utils";
import { log } from "./logger";

/**
 * These are Page Renderer feature flags for production environment for gradual enabling of new features.
 * Flags are enabled based on the appId hash. On local and staging environment all flags are enabled.
 * On any environment you can override the flag by adding it to the query string: ?FLAG_NAME=true/false
 *
 * FLAG_NAME => PERCENTAGE_OF_PROJECTS (0-100)
 *
 * To mock the flags in your tests use mockTrafficFeatureFlags/restoreTrafficFeatureFlags functions.
 *
 * @example
 * const FLAGS = {
 *   FLAG_NAME: 50,
 * };
 * */
const FLAGS = {};
export type FeatureFlag = keyof typeof FLAGS;

const FLAGS_VALUES: Record<string, boolean | undefined> = {};

const isLocalhostOrStaging =
    window.location.hostname === "localhost" || /staging(\d{0,2})\.softr\.(io|app)$/.test(window.location.hostname);

/**
 * Initializes feature flags based on the appId hash.
 * */
export function initTrafficFeatureFlags(appId: string) {
    const appIdHashPercentage = Math.abs(hashCode(appId)) % 100;
    const searchParams = new URLSearchParams(location.search);
    const enabledFlags: string[] = [];

    // @ts-expect-error: Suppressed automatically in TS 5.8 to 6 migration
    Object.entries(FLAGS).forEach(([flag, percentage]: [FeatureFlag, number]) => {
        if (searchParams.has(flag)) {
            FLAGS_VALUES[flag] = searchParams.get(flag) === "true";
        } else if (isLocalhostOrStaging) {
            FLAGS_VALUES[flag] = true;
        } else {
            FLAGS_VALUES[flag] = percentage > 0 && appIdHashPercentage < percentage;
        }

        if (FLAGS_VALUES[flag]) {
            enabledFlags.push(flag);
        }
    });

    if (enabledFlags.length) {
        log.info(`Experimental features enabled for this project: ${enabledFlags.join(", ")}`);
    }
}

/**
 * Checks if the feature flag is enabled.
 * */
export function isEnabled(flag: FeatureFlag): boolean {
    return !!FLAGS_VALUES[flag];
}
